import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { 
  GraduationCap, 
  User, 
  School, 
  Mail, 
  Lock, 
  ArrowLeft,
  AlertCircle
} from "lucide-react";

// Styled Components
const Container = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2.5rem 2rem;
  text-align: center;
  color: white;
`;

const HeaderTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const HeaderSubtitle = styled.p`
  font-size: 1rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 300;
`;

const Content = styled.div`
  padding: 2.5rem 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f9fafb;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f9fafb;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0.25rem;
  
  &:hover {
    color: #667eea;
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'secondary' ? `
    background: #f3f4f6;
    color: #374151;
    border: 2px solid #e5e7eb;
    
    &:hover {
      background: #e5e7eb;
      border-color: #d1d5db;
    }
  ` : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EnhancedStudentRegister: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    grade: "",
    school: ""
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.grade.trim()) {
      newErrors.grade = "Grade is required";
    }

    if (!formData.school.trim()) {
      newErrors.school = "School name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setLoading(true);

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        grade: formData.grade,
        school: formData.school,
        email: formData.email,
        password: formData.password,
        role: 'student'
      });

      toast.success("Registration successful! Please log in.");
      navigate("/auth/login");
    } catch (err: any) {
      toast.error(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/auth/unified-registration");
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <GraduationCap size={32} />
          Create Your Account
        </HeaderTitle>
        <HeaderSubtitle>
          Join our learning community today
        </HeaderSubtitle>
      </Header>

      <Content>
        <form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <User size={20} />
              Personal Information
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <InputContainer>
                  <InputIcon>
                    <User size={18} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="firstName"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("firstName", e.target.value)}
                  />
                </InputContainer>
                {errors.firstName && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.firstName}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <InputContainer>
                  <InputIcon>
                    <User size={18} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="lastName"
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("lastName", e.target.value)}
                  />
                </InputContainer>
                {errors.lastName && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.lastName}
                  </ErrorText>
                )}
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <Mail size={20} />
              Account Information
            </SectionTitle>
            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <InputContainer>
                <InputIcon>
                  <Mail size={18} />
                </InputIcon>
                                  <Input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("email", e.target.value)}
                  />
              </InputContainer>
              {errors.email && (
                <ErrorText>
                  <AlertCircle size={14} />
                  {errors.email}
                </ErrorText>
              )}
            </FormGroup>

            <FormGrid>
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <InputContainer>
                  <InputIcon>
                    <Lock size={18} />
                  </InputIcon>
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("password", e.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </PasswordToggle>
                </InputContainer>
                {errors.password && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.password}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <InputContainer>
                  <InputIcon>
                    <Lock size={18} />
                  </InputIcon>
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("confirmPassword", e.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "Hide" : "Show"}
                  </PasswordToggle>
                </InputContainer>
                {errors.confirmPassword && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.confirmPassword}
                  </ErrorText>
                )}
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <School size={20} />
              Academic Information
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="grade">Grade</Label>
                <InputContainer>
                  <InputIcon>
                    <GraduationCap size={18} />
                  </InputIcon>
                  <Select
                    id="grade"
                    value={formData.grade}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange("grade", e.target.value)}
                  >
                    <option value="">Enter your grade</option>
                    <option value="Grade 1">Grade 1</option>
                    <option value="Grade 2">Grade 2</option>
                    <option value="Grade 3">Grade 3</option>
                    <option value="Grade 4">Grade 4</option>
                    <option value="Grade 5">Grade 5</option>
                    <option value="Grade 6">Grade 6</option>
                    <option value="Grade 7">Grade 7</option>
                    <option value="Grade 8">Grade 8</option>
                    <option value="Grade 9">Grade 9</option>
                    <option value="Grade 10">Grade 10</option>
                    <option value="Grade 11">Grade 11</option>
                    <option value="Grade 12">Grade 12</option>
                  </Select>
                </InputContainer>
                {errors.grade && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.grade}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="school">School</Label>
                <InputContainer>
                  <InputIcon>
                    <School size={18} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="school"
                    placeholder="Enter your school name"
                    value={formData.school}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("school", e.target.value)}
                  />
                </InputContainer>
                {errors.school && (
                  <ErrorText>
                    <AlertCircle size={14} />
                    {errors.school}
                  </ErrorText>
                )}
              </FormGroup>
            </FormGrid>
          </FormSection>

          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={handleBack}>
              <ArrowLeft size={18} />
              Back to Selection
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </ButtonGroup>
        </form>
      </Content>
    </Container>
  );
};

export default EnhancedStudentRegister;
